import { useCallback, useState } from "react";
import { useSetGalleryInquiryContactMutation } from "../Slices/roomNateApis";
import { normalizeEmail } from "../utils/sessionAudience";

/** Matches the API's cap so a label can't be silently truncated server-side. */
const MAX_DISPLAY_NAME_CHARS = 200;

function readGuestSessionId() {
  try {
    return localStorage.getItem("mf_gsid") || null;
  } catch {
    return null;
  }
}

export function useGalleryUploaderContact({ layoutUuid, layoutMongoId }) {
  const [setGalleryInquiryContact] = useSetGalleryInquiryContactMutation();
  const [submitting, setSubmitting] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  const saveContact = useCallback(
    async (emailInput, displayNameInput) => {
      const email = normalizeEmail(emailInput);
      if (!email) {
        setError("Enter a valid email address.");
        return false;
      }
      if (!layoutUuid && !layoutMongoId) {
        setError("This layout is not ready yet.");
        return false;
      }

      setSubmitting(true);
      setError("");

      try {
        const displayName =
          typeof displayNameInput === "string"
            ? displayNameInput.replace(/\s+/g, " ").trim().slice(0, MAX_DISPLAY_NAME_CHARS)
            : "";

        await setGalleryInquiryContact({
          layoutUuid,
          layoutMongoId,
          inquiryContactEmail: email,
          // Omitted when blank rather than sent empty: the API reads an empty
          // string as "clear the label", so sending one would wipe a stored
          // label every time this modal is reopened to change only the email.
          ...(displayName ? { inquiryDisplayName: displayName } : {}),
          guestSessionId: readGuestSessionId(),
        }).unwrap();
        setSaved(true);
        return true;
      } catch (err) {
        const message =
          err?.data?.error ||
          err?.error ||
          "Something went wrong. Please try again.";
        setError(typeof message === "string" ? message : "Something went wrong.");
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [layoutUuid, layoutMongoId, setGalleryInquiryContact]
  );

  const reset = useCallback(() => {
    setSaved(false);
    setError("");
    setSubmitting(false);
  }, []);

  return { saveContact, submitting, saved, error, reset };
}
